import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import {
  createContext,
  useState,
  ReactNode,
  useEffect,
  useContext,
} from 'react';
import { apiKeyService, settingsService } from 'services';
import { getFetchStatus } from 'utils/status';

type ApiKeyContextData = {
  apiKeys: ApiKey[];
  partners: Partner[];
  status: LoadingStatus;
  fetchApiKeys: () => Promise<void>;
};

export const ApiKeyContext = createContext<ApiKeyContextData>({
  apiKeys: [],
  partners: [],
  status: 'NOT_LOADED' as LoadingStatus,
  // @ts-ignore
  fetchApiKeys: () => null,
});

const ApiKeyProvider = ({ children }: { children: ReactNode }) => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [apiKeysStatus, setApiKeysStatus] = useState(
    'NOT_LOADED' as LoadingStatus
  );

  const [partners, setPartners] = useState<Partner[]>([]);
  const [partnersStatus, setPartnersStatus] = useState(
    'NOT_LOADED' as LoadingStatus
  );

  const fetchApiKeys = async () => {
    try {
      setApiKeysStatus('LOADING');
      const { data: apiKeys } = await apiKeyService.getAll();
      setApiKeys(apiKeys);
      setApiKeysStatus('LOADED');
    } catch (error) {
      console.log(error);
      setApiKeysStatus('ERROR');
    }
  };

  const fetchPartners = async () => {
    try {
      setPartnersStatus('LOADING');
      const { data: partners } = await settingsService.getPartners();
      setPartners(partners);
      setPartnersStatus('LOADED');
    } catch (error) {
      console.log(error);
      setPartnersStatus('ERROR');
    }
  };

  useEffect(() => {
    fetchApiKeys();
    fetchPartners();
  }, []);

  const status = getFetchStatus(apiKeysStatus, partnersStatus);

  return (
    <ApiKeyContext.Provider
      value={{
        apiKeys,
        status,
        partners,
        fetchApiKeys,
      }}
    >
      {status === 'LOADED' ? children : <LoadingSpinner fill />}
    </ApiKeyContext.Provider>
  );
};

export const useApiKey = () => useContext(ApiKeyContext);

export default ApiKeyProvider;
